import { Box, Button, Chip, MenuItem, Paper, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { ServerDataGrid } from '../../shared/data/ServerDataGrid';
import type { PageResult } from '../../shared/data/types';
import { countActiveFilterValues } from '../../shared/filter/activeFilterCount';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { defaultCommissionRuleListQuery, commissionRulesRepositoryToken } from './repository';
import { toSafeError } from './CommissionRuleWizard';
import type {
  CommissionRuleFilters,
  CommissionRuleListQuery,
  CommissionRuleSummary,
} from './types';

export function CommissionRuleListPage({ dataSource }: { readonly dataSource: DataSource }) {
  const repository = useRepository(commissionRulesRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [query, setQuery] = useState(defaultCommissionRuleListQuery);
  const [refreshKey, setRefreshKey] = useState(0);
  const resource = useResource(
    (signal) => repository.list(query, signal),
    [query, refreshKey, repository],
    (page) => page.items.length === 0,
  );
  const canManage =
    auth.status === 'authenticated' &&
    auth.principal.permissions.includes('COMMISSION_RULE_MANAGE');
  const columns = useMemo<readonly GridColDef<CommissionRuleSummary>[]>(
    () => [
      {
        field: 'version',
        headerName: t('commissionRules.fields.version'),
        minWidth: 110,
        renderCell: ({ row }) => <Link to={`/commission-rules/${row.id}`}>v{row.version}</Link>,
      },
      {
        field: 'scope',
        headerName: t('commissionRules.fields.scope'),
        minWidth: 120,
        sortable: false,
      },
      {
        field: 'game',
        headerName: t('commissionRules.fields.game'),
        minWidth: 180,
        sortable: false,
        renderCell: ({ row }) => <Link to={`/games/${row.gameId}`}>{row.game.code}</Link>,
      },
      {
        field: 'partnerId',
        headerName: t('commissionRules.fields.partnerId'),
        minWidth: 250,
        sortable: false,
        renderCell: ({ row }) =>
          row.partnerId === null ? (
            '—'
          ) : (
            <Link to={`/partners/${row.partnerId}`}>{row.partnerId}</Link>
          ),
      },
      {
        field: 'agentRatePercent',
        headerName: t('commissionRules.fields.agentRatePercent'),
        minWidth: 150,
        sortable: false,
      },
      {
        field: 'affiliateRatePercent',
        headerName: t('commissionRules.fields.affiliateRatePercent'),
        minWidth: 160,
        sortable: false,
      },
      {
        field: 'effectiveFrom',
        headerName: t('commissionRules.fields.effectiveFrom'),
        minWidth: 190,
        valueFormatter: (value: string | null) =>
          value === null ? '—' : formatTimestamp(value, 'Asia/Bangkok', language).display,
      },
      {
        field: 'lifecycle',
        headerName: t('commissionRules.fields.lifecycle'),
        minWidth: 130,
        renderCell: ({ row }) => <RuleStatusChip value={row.lifecycle} />,
      },
      {
        field: 'phase',
        headerName: t('commissionRules.fields.phase'),
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }) => <RuleStatusChip value={row.phase} />,
      },
      {
        field: 'createdAt',
        headerName: t('commissionRules.fields.createdAt'),
        minWidth: 190,
        valueFormatter: (value: string) => formatTimestamp(value, 'Asia/Bangkok', language).display,
      },
      {
        field: 'createdBy',
        headerName: t('commissionRules.fields.createdBy'),
        minWidth: 250,
        sortable: false,
      },
      {
        field: 'publishedBy',
        headerName: t('commissionRules.fields.approvedBy'),
        minWidth: 250,
        sortable: false,
      },
    ],
    [language, t],
  );

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={
          <>
            <DataSourceBadge source={dataSource} />
            {canManage ? (
              <Button component={Link} to="/commission-rules/new" variant="contained">
                {t('commissionRules.actions.new')}
              </Button>
            ) : null}
          </>
        }
        subtitle={t('commissionRules.subtitle')}
        title={t('commissionRules.title')}
      />
      <RuleFilters
        onApply={(filters) => setQuery((current) => ({ ...current, filters, page: 1 }))}
        value={query.filters}
      />
      <PageState
        emptyMessage={t('commissionRules.empty')}
        error={
          resource.error === null
            ? null
            : toSafeError(resource.error, t('commissionRules.errors.load'), t)
        }
        onRetry={() => setRefreshKey((key) => key + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
            <CommissionRuleGrid
              aria-label={t('commissionRules.gridLabel')}
              columns={columns}
              onQueryChange={setQuery}
              page={resource.data}
              query={query}
            />
          </Paper>
        )}
      </PageState>
    </Box>
  );
}

function CommissionRuleGrid({
  columns,
  onQueryChange,
  page,
  query,
  ...props
}: {
  readonly columns: readonly GridColDef<CommissionRuleSummary>[];
  readonly onQueryChange: (query: CommissionRuleListQuery) => void;
  readonly page: PageResult<CommissionRuleSummary>;
  readonly query: CommissionRuleListQuery;
  readonly 'aria-label': string;
}) {
  const gridQuery = { ...query, page: query.page - 1 };
  return (
    <ServerDataGrid
      {...props}
      columns={columns}
      disableRowSelectionOnClick
      onQueryChange={(next) => onQueryChange({ ...next, page: next.page + 1 })}
      page={page}
      query={gridQuery}
      sx={{ minWidth: 1380 }}
    />
  );
}

function RuleFilters({
  onApply,
  value,
}: {
  readonly onApply: (filters: CommissionRuleFilters) => void;
  readonly value: CommissionRuleFilters;
}) {
  const { t } = useTranslation();
  const { handleSubmit, register, reset } = useForm<CommissionRuleFilters>({
    defaultValues: value,
  });
  const activeSecondaryFilterCount = countActiveFilterValues(
    {
      effectiveFrom: value.effectiveFrom,
      effectiveTo: value.effectiveTo,
      gameId: value.gameId,
      gameTypeId: value.gameTypeId,
    },
    {
      effectiveFrom: defaultCommissionRuleListQuery.filters.effectiveFrom,
      effectiveTo: defaultCommissionRuleListQuery.filters.effectiveTo,
      gameId: defaultCommissionRuleListQuery.filters.gameId,
      gameTypeId: defaultCommissionRuleListQuery.filters.gameTypeId,
    },
  );
  return (
    <ListFilterPanel
      activeSecondaryFilterCount={activeSecondaryFilterCount}
      ariaLabel={t('filterPanel.label', { page: t('commissionRules.title') })}
      onClear={() => {
        reset(defaultCommissionRuleListQuery.filters);
        onApply(defaultCommissionRuleListQuery.filters);
      }}
      onSubmit={handleSubmit(onApply)}
      primaryFields={
        <>
          <TextField
            label={t('commissionRules.fields.partnerId')}
            size="small"
            {...register('partnerId')}
          />
          <TextField
            defaultValue=""
            label={t('commissionRules.fields.scope')}
            select
            size="small"
            {...register('scope')}
          >
            <MenuItem value="">{t('commissionRules.filters.all')}</MenuItem>
            <MenuItem value="GLOBAL">GLOBAL</MenuItem>
            <MenuItem value="PARTNER">PARTNER</MenuItem>
          </TextField>
          <TextField
            defaultValue=""
            label={t('commissionRules.fields.lifecycle')}
            select
            size="small"
            {...register('lifecycle')}
          >
            <MenuItem value="">{t('commissionRules.filters.all')}</MenuItem>
            {['DRAFT', 'PUBLISHED', 'CANCELLED'].map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            defaultValue=""
            label={t('commissionRules.fields.phase')}
            select
            size="small"
            {...register('phase')}
          >
            <MenuItem value="">{t('commissionRules.filters.all')}</MenuItem>
            {['DRAFT', 'SCHEDULED', 'ACTIVE', 'EXPIRED', 'CANCELLED'].map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </TextField>
        </>
      }
      secondaryFields={
        <>
          <TextField
            label={t('commissionRules.fields.gameTypeId')}
            size="small"
            {...register('gameTypeId')}
          />
          <TextField
            label={t('commissionRules.fields.gameId')}
            size="small"
            {...register('gameId')}
          />
          <TextField
            label={t('commissionRules.filters.effectiveFrom')}
            size="small"
            type="datetime-local"
            slotProps={{ inputLabel: { shrink: true } }}
            {...register('effectiveFrom')}
          />
          <TextField
            label={t('commissionRules.filters.effectiveTo')}
            size="small"
            type="datetime-local"
            slotProps={{ inputLabel: { shrink: true } }}
            {...register('effectiveTo')}
          />
        </>
      }
    />
  );
}

export function RuleStatusChip({ value }: { readonly value: string }) {
  const color =
    value === 'ACTIVE' || value === 'PUBLISHED'
      ? 'success'
      : value === 'CANCELLED'
        ? 'default'
        : 'warning';
  return <Chip color={color} label={value} size="small" />;
}
